import React from 'react';
import './StatCard.css';

/**
 * StatCard component.
 *
 * Displays a single KPI metric inside a rounded card with three rows:
 * 1. **Header** — metric title on the left, icon badge on the right.
 * 2. **Body**   — large numeric value followed by a coloured delta badge
 *                 (green ▲ if positive, red ▼ if negative).
 * 3. **Footer** — small grey subtitle describing the reference period.
 *
 * While data is loading a shimmer skeleton replaces the value row.
 *
 * @component
 * @param {object}         props
 * @param {string}         props.title       - Label describing the metric (e.g. "New Clients").
 * @param {string}         props.icon        - Material Symbols icon name (e.g. "person_add").
 * @param {'orange'|'green'|'blue'|'purple'} [props.iconColor='orange']
 *                                           - Colour theme applied to the icon badge.
 * @param {string|number}  props.value       - Current metric value to display.
 * @param {number|null}    props.delta       - Percentage (or percentage-point) change vs. the
 *                                            reference period. Positive → green, negative → red,
 *                                            `null` → hidden.
 * @param {string}         props.subText     - Short label for the reference period
 *                                            (e.g. "Since last month").
 * @param {string}         [props.tooltip]   - Optional explanatory tooltip shown on the ℹ icon.
 * @param {boolean}        [props.loading]   - When `true` a shimmer skeleton is shown instead of
 *                                            the value. Defaults to `false`.
 * @returns {JSX.Element}
 */
const StatCard = ({ title, icon, iconColor = 'orange', value, delta, subText, tooltip, loading = false }) => {
    /** @type {boolean} */
    const deltaPositive = delta >= 0;

    return (
        <div className="sc-card">

            {/* ── Header: title + icon ── */}
            <div className="sc-header">
                <div className="sc-title-group">
                    <span className="sc-title">{title}</span>
                    {tooltip && (
                        <span
                            className="sc-info-icon material-symbols-outlined"
                            data-tooltip={tooltip}
                            aria-label={tooltip}
                        >
                            info
                        </span>
                    )}
                </div>
                <span className={`sc-icon sc-icon--${iconColor} material-symbols-outlined`}>
                    {icon}
                </span>
            </div>

            {/* ── Body: value + delta ── */}
            <div className="sc-body">
                {loading
                    ? <span className="sc-skeleton" />
                    : <span className="sc-value">{value}</span>
                }
                {!loading && delta !== null && (
                    <span className={`sc-delta ${deltaPositive ? 'sc-delta--up' : 'sc-delta--down'}`}>
                        {deltaPositive ? '▲' : '▼'} {Math.abs(delta)}%
                    </span>
                )}
            </div>

            {/* ── Footer: reference period label ── */}
            <p className="sc-sub">{subText}</p>

        </div>
    );
};

export default StatCard;
